import { z } from "zod";
import {
  SYMPTOM_ACUTE_SCREEN,
  SYMPTOM_CODES,
  SYMPTOM_QUESTIONS,
  SYMPTOM_RESOLVED_LABEL,
  SYMPTOM_SEVERITY_VALUES,
  type SymptomCode,
  type SymptomSafetyAnswers,
  type SymptomSafetyQuestion,
} from "./symptom-entry";

// TES-1257 (Ella 2.1 slice K) — the check-ins follow up the symptoms she
// follows. Drive "Ella 2.1 — Productdefinition …" §8:
// "Besvär som registreras i Besvärsguiden, vid en dos eller via direktregistrering skapar samma SymptomEntry. Nästa incheckning visar det befintliga besväret och frågar hur det är nu. Patientens uppdatering kompletterar historiken och skapar inte en kopia av ursprungsregistreringen."
// and "Saknat svar får aldrig tolkas som oförändrat."
//
// So F-04 and F-05 ask about each of her OPEN entries, and her answer is
// appended to that same entry (SymptomEntryService). The follow-ups are not
// fields of the published template: they are hers, served next to it
// (GET patient-api/v1/symptoms/follow-ups) and sent next to the answers
// (`symptomFollowUps` on the submission), so the published version, its
// validation schema and every reader of FormSubmission.answers stay as they
// are. A client that never asks for them sends none, and nothing is written:
// a missing answer is no answer, never "Oförändrat".

type Copy = { sv: string; en: string };

/** The check-ins that follow up her symptoms, and the surface each writes. */
export const SYMPTOM_FOLLOW_UP_FORMS = {
  weekly_checkin: "f04",
  milestone: "f05",
} as const;
export type SymptomFollowUpForm = keyof typeof SYMPTOM_FOLLOW_UP_FORMS;

export function isSymptomFollowUpForm(key: string): key is SymptomFollowUpForm {
  return Object.prototype.hasOwnProperty.call(SYMPTOM_FOLLOW_UP_FORMS, key);
}

// ---------------------------------------------------------------------------
// The question.
//
// "Hur är det nu?" is Drive §8's own words. The answer is SYMPTOM_TREND's
// "Bättre | Oförändrat | Värre" (§5.4) for a symptom she still has, plus her
// end state "Borta" (Ö-452: "Patienten kan markera ett besvär som Borta.
// Systemet får inte automatiskt skriva om historiska uppgifter till Borta.").
// "Nytt" and "Kommit tillbaka" are not offered: the entry is already open.
// A symptom she still has also answers SYMPTOM_SEVERITY ("Hur mycket besvär
// har du av detta just nu?"), the same question as every other report.

export const SYMPTOM_FOLLOW_UP_STATUS_VALUES = [
  "battre",
  "oforandrat",
  "varre",
  "borta",
] as const;
export type SymptomFollowUpStatus =
  (typeof SYMPTOM_FOLLOW_UP_STATUS_VALUES)[number];

const STILL_THERE = ["battre", "oforandrat", "varre"] as const;

export const SYMPTOM_FOLLOW_UP_STATUS_OPTIONS: {
  value: SymptomFollowUpStatus;
  label: Copy;
}[] = [
  ...STILL_THERE.map((value) => ({
    value,
    label: SYMPTOM_QUESTIONS.SYMPTOM_TREND.options.find(
      (o) => o.value === value,
    )!.label,
  })),
  { value: "borta", label: SYMPTOM_RESOLVED_LABEL },
];

const SV_WEEKDAY_AGO = [
  "i söndags",
  "i måndags",
  "i tisdags",
  "i onsdags",
  "i torsdags",
  "i fredags",
  "i lördags",
];
const EN_WEEKDAY = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const SV_MONTH = [
  "januari",
  "februari",
  "mars",
  "april",
  "maj",
  "juni",
  "juli",
  "augusti",
  "september",
  "oktober",
  "november",
  "december",
];
const EN_MONTH = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const TIME_ZONE = "Europe/Stockholm";

/** Her calendar day in Sweden, as UTC midnight of that day. */
function swedishDay(at: Date): Date {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(at);
  const part = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);
  return new Date(Date.UTC(part("year"), part("month") - 1, part("day")));
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * When she registered it, as she would say it: "idag", "igår", "i måndags"
 * within the past week, otherwise the date ("den 3 oktober"). Swedish days.
 */
export function symptomReportedWhen(reportedAt: Date, now: Date): Copy {
  const day = swedishDay(reportedAt);
  const days = Math.round((swedishDay(now).getTime() - day.getTime()) / DAY_MS);
  if (days <= 0) return { sv: "idag", en: "today" };
  if (days === 1) return { sv: "igår", en: "yesterday" };
  if (days < 7) {
    const weekday = day.getUTCDay();
    return { sv: SV_WEEKDAY_AGO[weekday]!, en: `on ${EN_WEEKDAY[weekday]}` };
  }
  const date = day.getUTCDate();
  const month = day.getUTCMonth();
  return {
    sv: `den ${date} ${SV_MONTH[month]}`,
    en: `on ${date} ${EN_MONTH[month]}`,
  };
}

const lowerFirst = (s: string, locale: string) =>
  s.charAt(0).toLocaleLowerCase(locale) + s.slice(1);

/**
 * "Du registrerade illamående i måndags. Hur är det nu?" — the symptom by its
 * register name and the day of her latest report on it.
 */
export function symptomFollowUpPrompt(
  label: Copy,
  reportedAt: Date,
  now: Date,
): Copy {
  const when = symptomReportedWhen(reportedAt, now);
  return {
    sv: `Du registrerade ${lowerFirst(label.sv, "sv")} ${when.sv}. Hur är det nu?`,
    en: `You registered ${lowerFirst(label.en, "en")} ${when.en}. How is it now?`,
  };
}

// ---------------------------------------------------------------------------
// Safety: the same screen as every other report (TES-1250), asked once.
//
// A follow-up she still has carries the safety answers for its symptom
// (safetyQuestionsFor). Where the check-in itself asks the SAME question, the
// follow-up takes her answer from the form instead of asking it twice:
// F-04's week_urgent_screen IS SYMPTOM_ACUTE_SCREEN and its
// week_symptom_impact IS SYMPTOM_KEEP_FOOD_DOWN, word for word
// (symptom-safety-parity.spec.ts in the api). F-05's month_urgent_signals has
// the same options but asked about "den senaste månaden", not "just nu", up to
// v6, so that answer is never reused as hers about today: there the follow-up
// asks. F-05 v7 (TES-1251) asks it "just nu", word for word the acute screen,
// and its answer is reused. Which one she answers is read from the field as
// her version asks it, never from a version number (those differ per
// database): an admin rewording of the field turns the reuse off, not on.
//
// `fieldKey` is also where the check-in routes an urgent follow-up answer: the
// form's own rule for that field raises the hard stop (week_urgent_* in F-04,
// month_urgent_* in F-05), so the acute path, the doctor's alert and the
// medical review case treat it exactly as the form's own answer. An urgent
// answer to "just nu" is also true of "den senaste månaden".

export interface CheckinSafetyField {
  /** The check-in's own field that asks the same question. */
  fieldKey: string;
  /**
   * Her answer to the form's field is her answer to the follow-up's, in
   * every version of the form.
   */
  reuseAnswer: boolean;
  /**
   * Otherwise it is, in a version whose field asks exactly this question:
   * the same words and the same options (checkinFieldAnswersSafety).
   */
  reuseWhenAsked?: SymptomSafetyQuestion;
}

/** A check-in field as the version she answers asks it. */
export interface CheckinAskedField {
  key: string;
  label: { sv: string };
  options?: readonly { value: string; label: { sv: string } }[];
}

/**
 * Whether her answer to the check-in's own field is her answer to the
 * safety question: always where the form's field is reused in every
 * version, and otherwise only where `formField` (the field in the version
 * she answers) asks `reuseWhenAsked` word for word.
 */
export function checkinFieldAnswersSafety(
  field: CheckinSafetyField,
  formField: CheckinAskedField | undefined,
): boolean {
  if (field.reuseAnswer) return true;
  const q = field.reuseWhenAsked;
  if (!q || !formField) return false;
  const options = (o: readonly { value: string; label: { sv: string } }[]) =>
    o.map((x) => `${x.value}\u0000${x.label.sv.trim()}`).join("\u0001");
  return (
    formField.label.sv.trim() === q.label.sv.trim() &&
    options(formField.options ?? []) === options(q.options)
  );
}

export const CHECKIN_SAFETY_FIELDS: Readonly<
  Record<SymptomFollowUpForm, Readonly<Record<string, CheckinSafetyField>>>
> = {
  weekly_checkin: {
    SYMPTOM_ACUTE_SCREEN: { fieldKey: "week_urgent_screen", reuseAnswer: true },
    SYMPTOM_KEEP_FOOD_DOWN: {
      fieldKey: "week_symptom_impact",
      reuseAnswer: true,
    },
  },
  milestone: {
    SYMPTOM_ACUTE_SCREEN: {
      fieldKey: "month_urgent_signals",
      reuseAnswer: false,
      reuseWhenAsked: SYMPTOM_ACUTE_SCREEN,
    },
  },
};

const answeredValue = (value: unknown): value is string | string[] =>
  (typeof value === "string" && value.length > 0) ||
  (Array.isArray(value) &&
    value.length > 0 &&
    value.every((v) => typeof v === "string"));

/**
 * The questions the check-in already asked her, by safety question id: the
 * reusable ones she answered in the form. `formAnswers` are the answers she
 * can see (a hidden field's answer is never sent); `formFields` are the
 * fields of the version she answers, without which only an always-reused
 * field counts. `onlyConditional` keeps just the ones reused by wording.
 */
export function reusedSafetyAnswers(
  form: SymptomFollowUpForm,
  formAnswers: Readonly<Record<string, unknown>>,
  formFields: readonly CheckinAskedField[] = [],
  onlyConditional = false,
): SymptomSafetyAnswers {
  const out: SymptomSafetyAnswers = {};
  for (const [id, field] of Object.entries(CHECKIN_SAFETY_FIELDS[form])) {
    if (onlyConditional && field.reuseAnswer) continue;
    const formField = formFields.find((f) => f.key === field.fieldKey);
    if (!checkinFieldAnswersSafety(field, formField)) continue;
    const value = formAnswers[field.fieldKey];
    if (answeredValue(value)) out[id] = value;
  }
  return out;
}

/**
 * A follow-up's safety answers: those she gave in the follow-up, with the
 * form's own answer in place wherever the form asked the same question, and
 * only the questions asked about this symptom.
 *
 * A field reused only by its wording (F-05's screen, v7 on) fills in a
 * question she did not answer in the follow-up, and never replaces one she
 * did: a build from before TES-1251 asks F-05's screen in the follow-up
 * too, and her answer there is about the symptom she follows.
 */
export function checkinFollowUpSafetyAnswers(
  form: SymptomFollowUpForm,
  formAnswers: Readonly<Record<string, unknown>>,
  questions: readonly SymptomSafetyQuestion[],
  given: SymptomSafetyAnswers | undefined,
  formFields: readonly CheckinAskedField[] = [],
): SymptomSafetyAnswers {
  const always = reusedSafetyAnswers(form, formAnswers);
  const byWording = reusedSafetyAnswers(form, formAnswers, formFields, true);
  const merged = {
    ...byWording,
    ...(given ?? {}),
    ...always,
  };
  const asked = new Set(questions.map((q) => q.id));
  return Object.fromEntries(
    Object.entries(merged).filter(([id]) => asked.has(id)),
  );
}

// ---------------------------------------------------------------------------
// GET patient-api/v1/symptoms/follow-ups

export interface SymptomFollowUpItem {
  entryId: string;
  symptomCode: SymptomCode;
  label: Copy;
  /** ISO-8601: her latest report on it, the day the prompt names. */
  lastReportedAt: string;
  /** "Du registrerade … . Hur är det nu?" */
  prompt: Copy;
  /** The safety questions for this symptom in this environment, in order. */
  safetyQuestions: SymptomSafetyQuestion[];
}

export interface SymptomFollowUpsView {
  statusOptions: typeof SYMPTOM_FOLLOW_UP_STATUS_OPTIONS;
  severityQuestion: typeof SYMPTOM_QUESTIONS.SYMPTOM_SEVERITY;
  /** Her open entries, newest first. Empty for a client without the contract. */
  followUps: SymptomFollowUpItem[];
}

// ---------------------------------------------------------------------------
// `symptomFollowUps` on POST patient-api/v1/forms/submissions

export const symptomFollowUpAnswerSchema = z
  .object({
    entryId: z.string().uuid(),
    status: z.enum(SYMPTOM_FOLLOW_UP_STATUS_VALUES),
    severity: z.enum(SYMPTOM_SEVERITY_VALUES).optional(),
    safetyAnswers: z
      .record(z.union([z.string(), z.array(z.string())]))
      .optional(),
  })
  .superRefine((a, ctx) => {
    if (a.status === "borta") {
      // "Borta" closes the entry and carries no answers (symptom_updates
      // CHECK: a resolved update has no severity and no safety answers).
      if (a.severity !== undefined || a.safetyAnswers !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "borta carries no severity or safety answers",
          path: ["status"],
        });
      }
      return;
    }
    if (a.severity === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "severity is required unless borta",
        path: ["severity"],
      });
    }
  });
export type SymptomFollowUpAnswer = z.infer<typeof symptomFollowUpAnswerSchema>;

/** One answer per entry; at most one open entry per symptom exists. */
export const symptomFollowUpAnswersSchema = z
  .array(symptomFollowUpAnswerSchema)
  .max(SYMPTOM_CODES.length)
  .superRefine((answers, ctx) => {
    const ids = answers.map((a) => a.entryId);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "one answer per entry",
      });
    }
  });
